import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Bottles, Recs, type Bottle } from '@/lib/api';
import { Badge, Card, EmptyState, Icon, Skeleton, type IconName } from '@/design-system';
import { BottleCard, BottleCardSkeleton } from '@/components/BottleCard';
import { Link } from 'react-router-dom';

interface Lane {
  id: 'drink-soon' | 'undiscovered' | 'pair-with';
  title: string;
  hint: string;
  icon: IconName;
}

export function RecommendPage() {
  const { t: _t } = useTranslation();

  const lanes: Lane[] = [
    { id: 'drink-soon', title: 'Drikkes snart', hint: 'Vinduet lukkes om kort tid', icon: 'clock' },
    { id: 'undiscovered', title: 'Glemt i kjelleren', hint: 'Du har ikke vurdert disse enda', icon: 'sparkles' },
    { id: 'pair-with', title: 'Topp paringer', hint: 'Best score mot dine siste retter', icon: 'utensils' },
  ];

  return (
    <div className="grid gap-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">For deg</h1>
          <p className="text-sm text-mist">Personlige anbefalinger fra din egen kjeller — ingen kjøp, bare det du allerede eier.</p>
        </div>
      </header>

      {lanes.map((lane) => (
        <Lane key={lane.id} lane={lane} />
      ))}
    </div>
  );
}

function Lane({ lane }: { lane: Lane }) {
  const recsQ = useQuery({
    queryKey: ['recs', lane.id],
    queryFn: () => Recs.list(lane.id, 8),
  });

  const bottleIds = (recsQ.data?.recommendations ?? []).map((r) => r.bottleId).filter(Boolean);
  const bottlesQ = useQuery({
    queryKey: ['recs.bottles', bottleIds.join(',')],
    queryFn: async () => {
      if (bottleIds.length === 0) return [] as Bottle[];
      const fetched = await Promise.all(bottleIds.map((id) => Bottles.get(id).catch(() => null)));
      return fetched.filter(Boolean) as Bottle[];
    },
    enabled: bottleIds.length > 0,
  });

  const map = new Map((bottlesQ.data ?? []).map((b) => [b.id, b]));

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-3">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-candle/15 text-candle">
          <Icon name={lane.icon} size={16} />
        </span>
        <div className="grid">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-serif text-xl text-parchment">{lane.title}</h2>
            {recsQ.data?.source && (
              <Badge tone={recsQ.data.source === 'openai' ? 'gold' : 'mist'}>
                {recsQ.data.source === 'openai' ? 'OpenAI' : 'Regler'}
              </Badge>
            )}
          </div>
          <span className="text-xs text-mist">
            {lane.hint}
            {recsQ.data?.ai?.enabled && recsQ.data.ai.used === false && recsQ.data.ai.error ? ' · OpenAI fallback aktiv' : ''}
          </span>
        </div>
      </div>

      {recsQ.isLoading ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="w-56 flex-shrink-0">
              <BottleCardSkeleton />
            </div>
          ))}
        </div>
      ) : (recsQ.data?.recommendations.length ?? 0) === 0 ? (
        <Card className="p-6">
          <EmptyState icon="bottle-empty" title="Ingen treff i denne kategorien enda" description="Legg til flere flasker, eller vurder noen — så blir det mer å anbefale." />
        </Card>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {recsQ.data!.recommendations.filter((rec) => Boolean(rec.bottleId)).map((rec) => {
            const b = map.get(rec.bottleId);
            return (
              <div key={rec.bottleId} className="w-60 flex-shrink-0">
                {b ? (
                  <Link to={`/bottle/${b.id}`} className="block">
                    <BottleCard bottle={b} highlight={rec.score > 25} />
                    {(rec.explanation || rec.reasons.length > 0) && (
                      <div className="mt-1 px-1 text-[10px] uppercase tracking-widest text-mist line-clamp-1">
                        {rec.explanation ?? rec.reasons.join(' · ')}
                      </div>
                    )}
                  </Link>
                ) : (
                  <Skeleton height={300} rounded="md" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.section>
  );
}
